import { useCallback, useMemo, useState } from 'react';
import type { User } from '@/types/admin';

export function useUserSelection(allUsers: User[], paginatedUsers: User[]) {
    const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(() => new Set());

    const isAllPaginatedUsersSelected =
        paginatedUsers.length > 0 && paginatedUsers.every((user) => selectedUserIds.has(String(user.id)));
    const isSomePaginatedUsersSelected =
        paginatedUsers.some((user) => selectedUserIds.has(String(user.id))) && !isAllPaginatedUsersSelected;

    const handleToggleUserSelection = useCallback((userId: User['id'], checked: boolean | 'indeterminate') => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            const key = String(userId);
            if (checked === true) {
                next.add(key);
            } else {
                next.delete(key);
            }
            return next;
        });
    }, []);

    const handleToggleCurrentPageSelection = useCallback((checked: boolean | 'indeterminate') => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            for (const user of paginatedUsers) {
                const key = String(user.id);
                if (checked === true) {
                    next.add(key);
                } else {
                    next.delete(key);
                }
            }
            return next;
        });
    }, [paginatedUsers]);

    const selectedStatusCounts = useMemo(() => {
        return allUsers.reduce(
            (counts, user) => {
                if (!selectedUserIds.has(String(user.id))) return counts;
                const status = user.status?.toLowerCase();
                if (status === 'active') counts.active += 1;
                if (status === 'inactive') counts.inactive += 1;
                return counts;
            },
            { active: 0, inactive: 0 }
        );
    }, [allUsers, selectedUserIds]);

    const clearSelectedUsers = useCallback(() => {
        setSelectedUserIds(new Set());
    }, []);

    return {
        clearSelectedUsers,
        handleToggleCurrentPageSelection,
        handleToggleUserSelection,
        isAllPaginatedUsersSelected,
        isSomePaginatedUsersSelected,
        selectedStatusCounts,
        selectedUserIds,
    };
}
